import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowDownToLine, ArrowUpToLine, BringToFront, Replace, RotateCcw, RotateCw, SendToBack, Trash2 } from "@fg/icons";
import { cn } from "@/lib/utils";

export interface ImageLook {
    /** 0..1, 1 = solid. */
    opacity: number;
    /** Degrees, clockwise. */
    rotation: number;
}

export type Stacking = "front" | "back" | "forward" | "backward";

/**
 * The floating bar over a selected picture in Edit mode: replace it (same box), turn it, make it see-through, and move it
 * in front of or behind the other things on the page. It follows the picture the way the text bar follows a text box.
 */
export function ImageMiniToolbar({
    getRect, look, onChange, onReplace, onArrange, onRemove
}: {
    getRect: () => DOMRect | null;
    look: ImageLook;
    onChange: (patch: Partial<ImageLook>) => void;
    onReplace: () => void;
    onArrange: (how: Stacking) => void;
    onRemove: () => void;
}) {
    const ref = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

    useEffect(() => {
        let frame = 0;
        let last = "";
        const tick = () => {
            const r = getRect();
            const el = ref.current;
            let next: { left: number; top: number } | null = null;
            if (r && r.width > 0) {
                const area = document.getElementById("container_viewerContainer")?.getBoundingClientRect();
                if (!area || (r.bottom > area.top && r.top < area.bottom)) {
                    const h = el?.offsetHeight ?? 36;
                    const w = el?.offsetWidth ?? 420;
                    let top = r.top - h - 8;
                    if (top < (area?.top ?? 0) + 4) top = r.bottom + 8;
                    next = { left: Math.min(Math.max(8, r.left - 2), window.innerWidth - w - 8), top };
                }
            }
            const key = next ? `${Math.round(next.left)},${Math.round(next.top)}` : "";
            if (key !== last) {
                last = key;
                setPos(next);
            }
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [getRect]);

    const [angle, setAngle] = useState(String(Math.round(look.rotation)));
    useEffect(() => setAngle(String(Math.round(look.rotation))), [look.rotation]);
    const commitAngle = () => {
        const n = Number(angle);
        if (Number.isFinite(n)) onChange({ rotation: n });
        else setAngle(String(Math.round(look.rotation)));
    };

    const btn = "flex h-7 w-7 items-center justify-center rounded hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
    const sep = <span className="mx-0.5 h-5 w-px bg-border" aria-hidden />;
    return createPortal(
        <div
            ref={ref}
            role="toolbar"
            aria-label="Picture"
            style={pos ? { left: pos.left, top: pos.top } : { left: 0, top: 0, visibility: "hidden" }}
            className="fixed z-50 flex items-center gap-0.5 whitespace-nowrap rounded-md border bg-card p-1 text-xs text-card-foreground shadow-lg"
        >
            <button type="button" onClick={onReplace} title="Replace this picture with another; it takes the same size and place" className="flex h-7 items-center gap-1.5 rounded px-2 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <Replace className="h-4 w-4" aria-hidden /> Replace
            </button>
            {sep}
            <button type="button" aria-label="Rotate left 90°" title="Rotate left 90°" onClick={() => onChange({ rotation: look.rotation - 90 })} className={btn}><RotateCcw className="h-4 w-4" aria-hidden /></button>
            <button type="button" aria-label="Rotate right 90°" title="Rotate right 90°" onClick={() => onChange({ rotation: look.rotation + 90 })} className={btn}><RotateCw className="h-4 w-4" aria-hidden /></button>
            <label className="flex items-center gap-1" title="Angle in degrees, clockwise (turned pictures are resized after setting 0°)">
                <input
                    aria-label="Angle"
                    value={angle}
                    onChange={(e) => setAngle(e.target.value)}
                    onBlur={commitAngle}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitAngle(); } }}
                    inputMode="numeric"
                    className="h-7 w-11 rounded border bg-background px-1 text-right tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
                °
            </label>
            {sep}
            <label className="flex items-center gap-1.5 px-1" title="Transparency: left is see-through, right is solid">
                <span className="text-muted-foreground">Opacity</span>
                <input
                    type="range"
                    min={5}
                    max={100}
                    step={5}
                    aria-label="Opacity"
                    value={Math.round(look.opacity * 100)}
                    onChange={(e) => onChange({ opacity: Number(e.target.value) / 100 })}
                    className="h-1 w-24 cursor-pointer accent-primary"
                />
                <span className="w-9 tabular-nums">{Math.round(look.opacity * 100)}%</span>
            </label>
            {sep}
            <button type="button" aria-label="Bring to front" title="Bring to front" onClick={() => onArrange("front")} className={btn}><BringToFront className="h-4 w-4" aria-hidden /></button>
            <button type="button" aria-label="Bring forward" title="Bring forward one step" onClick={() => onArrange("forward")} className={btn}><ArrowUpToLine className="h-4 w-4" aria-hidden /></button>
            <button type="button" aria-label="Send backward" title="Send backward one step" onClick={() => onArrange("backward")} className={btn}><ArrowDownToLine className="h-4 w-4" aria-hidden /></button>
            <button type="button" aria-label="Send to back" title="Send to back (behind the other added things)" onClick={() => onArrange("back")} className={btn}><SendToBack className="h-4 w-4" aria-hidden /></button>
            {sep}
            <button type="button" aria-label="Remove this picture" title="Remove" onClick={onRemove} className={cn(btn)}><Trash2 className="h-4 w-4" aria-hidden /></button>
        </div>,
        document.body
    );
}
